import { createFileRoute } from "@tanstack/react-router";
import { CostPageLayout } from "@/components/cost/CostPageLayout";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/skylight-repair-cost-phoenix";
const TITLE = "Skylight Repair Cost in Phoenix (2026) | Durafoam Roofing";
const DESCRIPTION =
  "What skylight repair and replacement actually cost in Phoenix — flashing resealing, glass replacement, and full skylight swaps, with what drives the price.";

const faqs = [
  { q: "How much does skylight repair cost in Phoenix?", a: "Most skylight reseals and flashing repairs run $350–$750. Glass or dome replacements on standard residential skylights typically land $600–$1,400. Full skylight replacement: $1,200–$3,500 depending on size and type." },
  { q: "Why are skylights leaking?", a: "9 times out of 10 it's the flashing, not the skylight itself. The seal between the skylight curb and the roof fails before the skylight does. We diagnose first, then quote." },
  { q: "Should I replace or repair?", a: "If the skylight is over 20 years old, replacement is usually the smart call — the rubber gaskets are at end of life. Newer skylights are almost always a reseal job." },
  { q: "What does a solar skylight or solar tube cost?", a: "It depends on the roof. A simple pull-off-the-old-unit swap to a new Velux solar skylight costs the least because the opening and curb are already there. Jobs that need a curb rebuild or re-tying into the existing roof cost more, and a solar tube is usually the most affordable option. We inspect the opening for free and give you exact pricing plus the best skylight options for the room." },
  { q: "Do solar skylights qualify for a tax credit?", a: "Velux solar-powered skylights may qualify for the federal residential clean energy tax credit because they run on solar power. Eligibility and credit amounts change, so we confirm current eligibility at your inspection and hand over the manufacturer documentation you need to claim it." },
];

export const Route = createFileRoute("/skylight-repair-cost-phoenix")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) =>
    serviceHead({ override: loaderData?.seo,
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: "Skylight Repair",
      faqs,
      rating: loaderData?.rating,
      reviewCount: loaderData?.reviewCount,
    }),
  component: Page,
});

function Page() {
  return (
    <CostPageLayout
      pageSlug="skylight-repair-cost-phoenix"
      eyebrow="Phoenix cost guide"
      h1="Skylight Repair Cost in Phoenix"
      subhead="What Phoenix homeowners actually pay for skylight repairs in 2026 — and when a replacement saves you money."
      heroBullets={[
        "Reseal / flashing: $350–$750",
        "Glass or dome replacement: $600–$1,400",
        "Full skylight swap: $1,200–$3,500",
        "Free diagnostic inspection",
      ]}
      intro={
        <>
          <p>
            Most leaking skylights in Phoenix aren't leaking through the glass — they're leaking
            around the flashing. A reseal and flashing repair on a standard residential skylight
            runs <strong>$350–$750</strong>. If the glass or dome itself is cracked, expect{" "}
            <strong>$600–$1,400</strong> for replacement of just that pane.
          </p>
          <p>
            Full skylight replacement (frame, flashing, glass) ranges{" "}
            <strong>$1,200–$3,500</strong> depending on size and type — fixed, vented, or solar
            tube. We'll tell you honestly which makes sense for your situation.
          </p>
        </>
      }
      pricingRows={[
        { label: "Reseal & flashing repair", range: "$350–$750", notes: "Most common fix" },
        { label: "Glass / dome replacement", range: "$600–$1,400", notes: "Single pane" },
        { label: "Full skylight replacement (fixed)", range: "$1,200–$2,200", notes: "Standard residential" },
        { label: "Full skylight replacement (vented)", range: "$1,800–$3,500", notes: "Operable units" },
        { label: "Solar skylight swap (Velux solar-powered)", range: "Varies", notes: "Simple swap costs less; curb/roof work costs more — inspection for exact price" },
        { label: "Solar tube / sun tunnel install", range: "Varies", notes: "Most affordable daylight option — price depends on tube size and install" },
      ]}
      pricingFootnote={
        <>
          Exact solar skylight and solar tube pricing depends on what we find on the roof — a clean
          pull-and-swap costs less than a job that needs a curb rebuild or re-tying into the existing
          roof. A free inspection gives you exact numbers and the best skylight options for the opening.
        </>
      }
      factors={[
        { title: "Skylight age", body: "Over 20 years old? Replace. Rubber gaskets are at end of life and you'll be back for another repair within a year or two." },
        { title: "Roof type", body: "Tile roofs require lifting and resetting tiles around the skylight curb — more labor than foam or shingle." },
        { title: "Brand", body: "Velux and similar quality brands have long-life gaskets and standard parts. Off-brand units often need full replacement to fix right." },
        { title: "Access", body: "Steep roofs and multi-story homes add staging time. Most Phoenix single-story homes are straightforward." },
      ]}
      faqs={faqs}
      ctaTitle="Get your skylight diagnosed"
      ctaBody="Use the calculator, or schedule a free inspection — we'll find the actual leak source and quote the right fix."
    />
  );
}
